import { CoverageDisplayEnum } from '@app/core/enums/coverage-display.enum';
import {
    CoverageMergeOptionsInterface,
    CoverageMergePriceOptionsInterface,
    CoverageRemoveOptionsInterface,
    CoverageRenameOptionsInterface,
} from '@app/core/interfaces';
import { CoverageIdEnum, CoverageSpecificationInterface } from 'outshared-lib';
import { CoveragePriceService } from '../services';

export class CoverageTransformBuilder {

    private coveragePriceService: CoveragePriceService;
    private coverages: CoverageSpecificationInterface[];

    constructor(coverages: CoverageSpecificationInterface[]) {
        this.coveragePriceService = new CoveragePriceService();
        this.coverages = coverages;
    }

    public order(
        options: { [id: string]: number },
    ): CoverageTransformBuilder {
        if (!options) {
            return this;
        }

        this.coverages = this.coverages.sort((firstCoverage, secondCoverage) => {
            const firstOrder = options[firstCoverage.coverageId];
            const secondOrder = options[secondCoverage.coverageId];

            return firstOrder - secondOrder;
        });

        return this;
    }

    public filterByDisplay(
        display: CoverageDisplayEnum,
    ): CoverageTransformBuilder {
        if (display === CoverageDisplayEnum.Future) {
            this.coverages = this.coverages.filter(this.filterFuture);
        } else {
            this.coverages = this.coverages.filter(this.filterCurrent);
        }

        return this;
    }

    public filterUnnecessary(): CoverageTransformBuilder {
        this.coverages = this.coverages.filter(this.filterUnnecessaryCoverage);
        return this;
    }

    public mergePrice(
        options: CoverageMergePriceOptionsInterface[],
    ): CoverageTransformBuilder {
        if (!options) {
            return this;
        }

        options.forEach((option) => this.coverages = this.mergePriceCoverage(this.coverages, option));

        return this;
    }

    public remove(
        options: CoverageRemoveOptionsInterface,
    ): CoverageTransformBuilder {
        if (!options) {
            return this;
        }

        const { match, removeIfMatch } = options;
        const matched: CoverageSpecificationInterface = this.coverages
            .find((coverage) => match.includes(coverage.coverageId));

        if (matched) {
            this.coverages = this.coverages.filter((coverage) => !removeIfMatch
                .includes(coverage.coverageId));
        }

        return this;
    }

    public merge(
        options: CoverageMergeOptionsInterface,
    ): CoverageTransformBuilder {
        if (!options) {
            return this;
        }

        const { match, mergeIfMatch, overrideBy } = options;
        const matched: CoverageSpecificationInterface = this.coverages
            .find((coverage) => match.includes(coverage.coverageId));
        const toMerge: CoverageSpecificationInterface = this.coverages
            .find((coverage) => mergeIfMatch.includes(coverage.coverageId));
        const toOverrideBy: CoverageSpecificationInterface = this.coverages
            .find((coverage) => overrideBy === coverage.coverageId);


        if (matched && toMerge) {
            if (!toOverrideBy) {
                const merged = this.mergeCoverage(toMerge, matched);
                this.coverages.push(merged);
            }

            const coveragesToRemove = toOverrideBy ? [toMerge.coverageId] : [toMerge.coverageId, matched.coverageId];
            this.coverages = this.coverages
                .filter((coverage) => !coveragesToRemove.includes(coverage.coverageId));
        }

        return this;
    }

    public rename(
        options: CoverageRenameOptionsInterface,
    ): CoverageTransformBuilder {
        if (!options) {
            return this;
        }

        const { match, renameIfMatch, rename } = options;
        const matched: CoverageSpecificationInterface = this.coverages
            .find((coverage) => match.includes(coverage.coverageId));
        const toRename: CoverageSpecificationInterface = this.coverages
            .find((coverage) => renameIfMatch.includes(coverage.coverageId));

        if (matched && toRename) {
            const renamed = this.renameCoverage(toRename, rename);
            this.coverages.push(renamed);
            this.coverages = this.coverages.filter((coverage) => ![toRename.coverageId].includes(coverage.coverageId));
        }

        return this;
    }

    public build(): CoverageSpecificationInterface[] {
        return this.coverages;
    }

    private mergeCoverage(
        source1: CoverageSpecificationInterface,
        source2: CoverageSpecificationInterface,
    ): CoverageSpecificationInterface {
        const coverageDescription = `${source1.coverageDescription.split(' ')[0]}`;
        const overrides = {
            adnCoverageCode: null,
            coverageId: null,
            coverageDescription: coverageDescription,
            purchasedCoverage: null,
            purchasedCoverage_future: null,
        };
        const merged: CoverageSpecificationInterface = {
            ...source1,
            ...source2,
            ...overrides,
        };

        return merged;
    }

    private mergePriceCoverage(
        coverages: CoverageSpecificationInterface[],
        options: CoverageMergePriceOptionsInterface,
    ): CoverageSpecificationInterface[] {
        if (!options) {
            return coverages;
        }

        const { match, mergePriceIfMatch } = options;
        const matched: CoverageSpecificationInterface = coverages
            .find((coverage) => match.includes(coverage.coverageId));
        const toMerge: CoverageSpecificationInterface = coverages
            .find((coverage) => mergePriceIfMatch.includes(coverage.coverageId));

        if (matched && toMerge) {
            const merged = this.coveragePriceService.mergePremium(matched, toMerge);

            coverages = coverages.filter((coverage) => matched.coverageId !== coverage.coverageId);
            coverages.push(merged);

            return coverages;
        }

        return coverages;
    }

    private renameCoverage(coverage: CoverageSpecificationInterface, rename: string): CoverageSpecificationInterface {
        const coverageDescription = rename;
        const overrides = {
            adnCoverageCode: null,
            coverageId: null,
            coverageDescription,
            purchasedCoverage: null,
            purchasedCoverageFuture: null,
        };

        return {
            ...coverage,
            ...overrides,
        };
    }

    private filterCurrent(coverage: CoverageSpecificationInterface): boolean {
        return !!coverage.purchasedCoverage;
    }

    private filterFuture(coverage: CoverageSpecificationInterface): boolean {
        if (coverage.purchasedCoverageFuture) {
            return true;
        }

        return coverage.purchasedCoverage && !coverage.purchasedCoverage.endDate;
    }

    private filterUnnecessaryCoverage(coverage: CoverageSpecificationInterface): boolean {
        return ![CoverageIdEnum.CarRedressLegalAid, CoverageIdEnum.MotorRedressLegalAid].includes(coverage.coverageId);
    }
}
